/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */
// tslint:disable:no-bitwise
import { Object3D, PlaneBufferGeometry, MeshBasicMaterial, Mesh, FrontSide, Matrix4, Vector3 } from 'three';
import { SEGMENT_ELEMENT_SIZE } from './constants';
var PartSize;
(function (PartSize) {
    PartSize[PartSize["SmallWidth"] = 1] = "SmallWidth";
    PartSize[PartSize["LargeWidth"] = 2] = "LargeWidth";
    PartSize[PartSize["SmallHeight"] = 4] = "SmallHeight";
    PartSize[PartSize["LargeHeight"] = 8] = "LargeHeight";
})(PartSize || (PartSize = {}));
/**
 * Enum representing the ooperation for one SegmentPart
 */
export var BoundingBoxOperation;
(function (BoundingBoxOperation) {
    BoundingBoxOperation[BoundingBoxOperation["Translate"] = 0] = "Translate";
    BoundingBoxOperation[BoundingBoxOperation["ScaleTranslate"] = 1] = "ScaleTranslate";
})(BoundingBoxOperation || (BoundingBoxOperation = {}));
/**
 * class representing one edit area
 */
export class SegmentPart extends Mesh {
    constructor(partSize, operationVector, rotationAxis, operation, geometry, material) {
        super(geometry, material);
        this.partSize = partSize;
        this.operationVector = operationVector;
        this.rotationAxis = rotationAxis;
        this.operation = operation;
    }
}
/**
 * This class represents one surface of a bounding box
 */
export class SegmentPlane extends Object3D {
    constructor(rotationAxis, angle, side = FrontSide) {
        super();
        this.rotateOnAxis(rotationAxis, angle);
        const createPart = (operationVector, partSize, operation) => {
            const segmentGeometry = new PlaneBufferGeometry(1, 1);
            const segmentMaterial = new MeshBasicMaterial({
                color: 0xDEDEDE,
                transparent: true,
                opacity: 0.4,
                side,
                visible: false,
                // I'm not sure if the polygon offset always works with every near/far setting of the camera
                // more informations see: https://sites.google.com/site/threejstuts/home/polygon_offset
                polygonOffset: true,
                polygonOffsetFactor: -1,
            });
            const mesh = new SegmentPart(partSize, operationVector, rotationAxis, operation, segmentGeometry, segmentMaterial);
            segmentGeometry.computeBoundingBox();
            // move pivot point of segment part for easier handling
            segmentGeometry.applyMatrix(new Matrix4().makeTranslation(1 * -0.5 * operationVector.x, 1 * -0.5 * operationVector.y, 0));
            // Because the pivot element of each part is not in the middle,
            // but at the respective edge (see comment below, the pivot elements are marked with a #)
            // a translation is just the 0.5 of the vector
            mesh.translateX(operationVector.x * 0.5);
            mesh.translateY(operationVector.y * 0.5);
            // rotate operation vector to match orientation in segmentPlane system
            operationVector.applyAxisAngle(rotationAxis, angle).round();
            mesh.addEventListener('click', () => {
                this.dispatchEvent({
                    type: 'click', detail: {
                        operationVector,
                        operation,
                        rotationAxis,
                    },
                });
            });
            return mesh;
        };
        // The SegmentPlane has a normalized size (width:1 and height:1)
        // and consists out of 9 SegmentParts
        // Each SegmentPart has also a normalized size (width:1 and height:1)
        // And each SegmentPart has a vector that defines
        //   - the direction in which the editor can edit the object.
        //   - the position of the SegmentPart inside the SegmentPlane
        //   - the pivot point of the SegmentPart (marked with a '#')
        // 
        //      #---------#--------#
        //      |0|       1      |2|
        //      |------------------|
        //      | |              | |
        //      | |              | |
        //      #3|       4#     |5#
        //      | |              | |
        //      | |              | |
        //      |------------------|
        //      |6|       7      |8|
        //      #---------#--------#
        this._segmentParts = [
            createPart(new Vector3(1, -1), PartSize.SmallWidth | PartSize.SmallHeight, BoundingBoxOperation.ScaleTranslate),
            createPart(new Vector3(0, -1), PartSize.LargeWidth | PartSize.SmallHeight, BoundingBoxOperation.ScaleTranslate),
            createPart(new Vector3(-1, -1), PartSize.SmallWidth | PartSize.SmallHeight, BoundingBoxOperation.ScaleTranslate),
            createPart(new Vector3(1, 0), PartSize.SmallWidth | PartSize.LargeHeight, BoundingBoxOperation.ScaleTranslate),
            createPart(new Vector3(0, 0), PartSize.LargeWidth | PartSize.LargeHeight, BoundingBoxOperation.Translate),
            createPart(new Vector3(-1, 0), PartSize.SmallWidth | PartSize.LargeHeight, BoundingBoxOperation.ScaleTranslate),
            createPart(new Vector3(1, 1), PartSize.SmallWidth | PartSize.SmallHeight, BoundingBoxOperation.ScaleTranslate),
            createPart(new Vector3(0, 1), PartSize.LargeWidth | PartSize.SmallHeight, BoundingBoxOperation.ScaleTranslate),
            createPart(new Vector3(-1, 1), PartSize.SmallWidth | PartSize.SmallHeight, BoundingBoxOperation.ScaleTranslate),
        ];
        this.add(...this._segmentParts);
    }
    getSegmentParts() {
        return this._segmentParts;
    }
    /**
     * Adjust the aspect ratio of the SegmentParts to the aspect ratio of the SegmentPlane.
     * @param axisToScale
     */
    setRatio(ratio, axisToScale) {
        // the sizes of the parts are given as percentages. (SEGMENT_ELEMENT_SIZE)
        // if the ratio of the sides of a segment is not symmetrical, the Segment looks like:
        // without adjusting the aspect ratio (The SegmentParts are getting asymmetric too)
        //      +----------------------------------+
        //      |   0   |         1        |   2   |
        //      |----------------------------------|
        //      |       |                  |       |
        //      |       |                  |       |
        //      |   3   |         4        |   5   |
        //      |       |                  |       |
        //      |       |                  |       |
        //      |----------------------------------|
        //      |   6   |         7        |   8   |
        //      |----------------------------------+
        // with adjusting the aspect ratio:
        //      +-----------------+----------------+
        //      |0|               1              |2|
        //      |----------------------------------|
        //      | |                              | |
        //      | |                              | |
        //      |3|               4#             |5|
        //      | |                              | |
        //      | |                              | |
        //      |----------------------------------|
        //      |6|               7              |8|
        //      +----------------------------------+
        let smallX = SEGMENT_ELEMENT_SIZE;
        let smallY = SEGMENT_ELEMENT_SIZE;
        let bigX = 1 - 2 * smallX;
        let bigY = 1 - 2 * smallY;
        if (axisToScale === 'y') {
            smallY = SEGMENT_ELEMENT_SIZE * ratio;
            bigY = 1 - 2 * smallY;
        }
        else {
            smallX = SEGMENT_ELEMENT_SIZE * ratio;
            bigX = 1 - 2 * smallX;
        }
        this._segmentParts.forEach((part) => {
            if ((part.partSize & PartSize.SmallHeight) === (PartSize.SmallHeight)) {
                part.scale.setY(smallY);
            }
            if ((part.partSize & PartSize.LargeHeight) === (PartSize.LargeHeight)) {
                part.scale.setY(bigY);
            }
            if ((part.partSize & PartSize.SmallWidth) === (PartSize.SmallWidth)) {
                part.scale.setX(smallX);
            }
            if ((part.partSize & PartSize.LargeWidth) === (PartSize.LargeWidth)) {
                part.scale.setX(bigX);
            }
        });
    }
}
//# sourceMappingURL=SegmentPlane.js.map